import type { HTMLAttributes } from 'react';
import { SparkIcon } from '../SparkIcon/SparkIcon';
import './AttachmentThumbnail.css';

export interface AttachmentThumbnailProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onError'> {
  /** Image source (object URL or remote URL). Rendered `object-fit: cover`. */
  src: string;
  /** File name; used as the image alt text and in the remove button label. */
  name: string;
  /** Shows the top-right remove pill and is called when it is clicked. */
  onRemove?: () => void;
  /** Disables the remove pill (e.g. while the form is submitting). */
  disabled?: boolean;
}

/**
 * 72px square image preview with a translucent top-right remove pill
 * (Figma "Share your thoughts" 9261:8847). Designed to sit in the
 * `InputTextArea` `attachments` slot for dropped screenshots.
 */
export function AttachmentThumbnail({
  src,
  name,
  onRemove,
  disabled = false,
  className = '',
  ...rest
}: AttachmentThumbnailProps) {
  const classes = ['spark-attachment-thumbnail', className].filter(Boolean).join(' ');
  return (
    <div className={classes} {...rest}>
      <img className="spark-attachment-thumbnail-image" src={src} alt={name} draggable={false} />
      {onRemove ? (
        <button
          type="button"
          className="spark-attachment-thumbnail-remove"
          aria-label={`Remove ${name}`}
          disabled={disabled}
          onClick={onRemove}
        >
          <SparkIcon slug="close" size="small" />
        </button>
      ) : null}
    </div>
  );
}
